import { Component, inject, effect, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { OptionModal, OptionsModalService } from 'src/utils/options-modal.service';
import { ButtonsComponent } from "../buttons/buttons.component";
import { ButtonType } from 'src/ui/types/button-size.type';
import { ModalService } from 'src/utils/modal.service';

@Component({
  selector: 'app-options-modal',
  standalone: true,
  imports: [CommonModule, ButtonsComponent],
  templateUrl: './options-modal.component.html',
})
export class OptionsModalComponent implements OnDestroy {
  modalOptionsService = inject(OptionsModalService);

  private initialSnapshot: string | null = null;
  public isDirty = false;
  private clearSnapshotTimer: any = null;
  private readonly CLEAR_DELAY = 300;

  get visible(): boolean { return this.modalOptionsService.modalVisible(); }
  get title(): string { return this.modalOptionsService.title(); }
  get subtitle(): string { return this.modalOptionsService.subtitle(); }
  get layout(): string { return this.modalOptionsService.modalLayout(); }
  get options(): OptionModal[] { return this.modalOptionsService.options(); }
  get selected(): OptionModal | null {
    const layout = this.modalOptionsService.modalLayout();
    if (layout === 'categories') {
      return this.isDirty ? ({ title: 'dummy' } as OptionModal) : null;
    }

    return this.modalOptionsService.selectedOption();
  }

  get btnCancel(): boolean { return this.modalOptionsService.btnCancelar(); }
  get cancelText(): string { return this.modalOptionsService.cancelButtonText(); }
  get continueText(): string { return this.modalOptionsService.continueButtonText(); }
  get showFooter(): boolean { return this.modalOptionsService.showFooter(); }
  get cancelType(): ButtonType { return this.modalOptionsService.cancelButtonType(); }
  get continueType(): ButtonType { return this.modalOptionsService.continueButtonType(); }
  get showModalDisclaimer(): boolean { return this.modalOptionsService.showModalDisclaimer(); }

  expandedKeys = new Set<string>();

  private _openEffectDestroy?: () => void;
  private _watchEffectDestroy?: () => void;
  private modalService = inject(ModalService);

  constructor() {
    const openEffect = effect(() => {
      const visible = this.modalOptionsService.modalVisible(); // track
      console.debug('[OptionsModal][effect-open] visible=', visible);

      if (visible) {
        if (this.clearSnapshotTimer) {
          clearTimeout(this.clearSnapshotTimer);
          this.clearSnapshotTimer = null;
          console.debug('[OptionsModal] cancel clear timer (reopen)');
        }

        if (this.initialSnapshot === null) {
          this.initialSnapshot = this.buildSnapshot();
          this.isDirty = false;
          console.debug('[OptionsModal] captured initialSnapshot=', this.initialSnapshot);
        } else {
          const current = this.buildSnapshot();
          this.isDirty = current !== this.initialSnapshot;
          console.debug('[OptionsModal] reopen compare current=', current, 'initial=', this.initialSnapshot, 'isDirty=', this.isDirty);
        }
      } else {
        if (this.clearSnapshotTimer) {
          clearTimeout(this.clearSnapshotTimer);
          this.clearSnapshotTimer = null;
        }
        console.debug('[OptionsModal] scheduling snapshot clear in', this.CLEAR_DELAY, 'ms');
        this.clearSnapshotTimer = setTimeout(() => {
          this.initialSnapshot = null;
          this.isDirty = false;
          this.clearSnapshotTimer = null;
          console.debug('[OptionsModal] cleared snapshot (timer)');
        }, this.CLEAR_DELAY);
      }
    });

    const watchEffect = effect(() => {
      const visible = this.modalOptionsService.modalVisible();
      const opts = this.modalOptionsService.options();
      console.debug('[OptionsModal][effect-watch] visible=', visible, 'options.len=', (opts || []).length);

      if (!visible) {
        return;
      }

      if (this.initialSnapshot === null) {
        console.debug('[OptionsModal][effect-watch] no initial snapshot yet, skipping comparison');
        return;
      }

      const current = this.buildSnapshot();
      const wasDirty = this.isDirty;
      this.isDirty = current !== this.initialSnapshot;

      if (wasDirty !== this.isDirty) {
        console.debug('[OptionsModal][effect-watch] isDirty changed from', wasDirty, 'to', this.isDirty);
      }

      console.debug('[OptionsModal][effect-watch] current=', current);
      console.debug('[OptionsModal][effect-watch] initial=', this.initialSnapshot);
      console.debug('[OptionsModal][effect-watch] isDirty=', this.isDirty);
    });

    this._openEffectDestroy = () => { try { (openEffect as any).destroy?.(); } catch { } };
    this._watchEffectDestroy = () => { try { (watchEffect as any).destroy?.(); } catch { } };
  }

  ngOnDestroy(): void {
    if (this.clearSnapshotTimer) {
      clearTimeout(this.clearSnapshotTimer);
      this.clearSnapshotTimer = null;
    }
    if (this._openEffectDestroy) this._openEffectDestroy();
    if (this._watchEffectDestroy) this._watchEffectDestroy();
  }

  backdropClose(): void {
    console.debug('[OptionsModal] backdropClose()');
    this.modalOptionsService.hide();
  }

  close(): void {
    console.debug('[OptionsModal] close()');
    this.modalOptionsService.cancel();
  }

  confirm(): void {
    console.debug('[OptionsModal] confirm() - clearing snapshot and calling service.confirm()');
    if (this.clearSnapshotTimer) {
      clearTimeout(this.clearSnapshotTimer);
      this.clearSnapshotTimer = null;
    }
    this.initialSnapshot = null;
    this.isDirty = false;
    this.modalOptionsService.confirm();
  }

  select(opt: OptionModal): void {
    console.debug('[OptionsModal] select()', opt);
    this.modalOptionsService.selectOption(opt);
    if (!this.showFooter) {
      this.modalOptionsService.confirm();
    }
  }

  toggleExpandFor(cat: OptionModal): void {
    const key = cat.title;
    if (this.expandedKeys.has(key)) {
      this.expandedKeys.delete(key);
    } else {
      this.expandedKeys.add(key);
    }
  }

  isExpanded(cat: OptionModal): boolean {
    return this.expandedKeys.has(cat.title);
  }

  addCategory(): void {
    console.debug('[OptionsModal] addCategory requested');
    this.modalOptionsService.requestAddCategory();
  }


  private buildSnapshot(): string {
    const opts = this.modalOptionsService.options();
    if (!opts || opts.length === 0) return '';

    const minimal = opts
      .map(o => ({
        title: o.title ?? '',
        value: (o as any).value ?? (o as any).id ?? '',
        items: Array.isArray(o.items) ? [...o.items].sort() : []
      }))
      .sort((a, b) => {
        const aKey = a.value || a.title;
        const bKey = b.value || b.title;
        return String(aKey).localeCompare(String(bKey));
      });

    try {
      return JSON.stringify(minimal);
    } catch {
      return String(minimal);
    }
  }

  public shouldEnableContinue(): boolean {
    const layout = this.modalOptionsService.modalLayout();

    if (layout === 'categories') {
      console.debug('[OptionsModal] shouldEnableContinue (categories):', this.isDirty);
      return this.isDirty;
    }

    if (layout === 'select-vertical' || layout === 'options-horizontal') {
      return !!this.modalOptionsService.selectedOption();
    }

    return true;
  }


  onAddFieldClick(event: Event, cat: OptionModal): void {
  event.stopPropagation();
  if (!cat || (cat as any).editable === false) {
    console.debug('[OptionsModal] onAddFieldClick ignored (not editable)', cat);
    return;
  }
  console.debug('[OptionsModal] requestAddField for', cat.title);
  this.modalOptionsService.requestAddField(cat.title);
}

removeCategory(cat: OptionModal): void {
  if (!cat || (cat as any).editable === false) {
    console.debug('[OptionsModal] removeCategory ignored (not editable)', cat);
    return;
  }
  console.debug('[OptionsModal] requestDeleteCategory for', cat.title);
  this.modalOptionsService.requestDeleteCategory(cat.title);
}

removeFieldFromCategory(cat: OptionModal, fi: number): void {
  if (!cat || (cat as any).editable === false) {
    console.debug('[OptionsModal] removeFieldFromCategory ignored (not editable)', cat);
    return;
  }
  console.debug('[OptionsModal] requestDeleteField for', cat.title, 'index', fi);
  this.modalOptionsService.requestDeleteField(cat.title, fi);
}

 onCancelClick(): void {
    console.debug('[OptionsModal] onCancelClick() layout=', this.layout);

    if (this.layout === 'categories') {
      this.confirmCancelModal();
      return;
    }
    this.modalOptionsService.cancel();
    this.modalOptionsService.hide?.();
  }

  private confirmCancelModal(){
     this.modalService.showModal({
    modalType: 'info',
    iconClass: 'fi fi-sr-exclamation text-warning text-2xl-plus',
    title: '¿Deseas salir sin guardar los cambios?',
    btnCancelar: true,
    cancelButtonText: 'Cancelar',
    continueButtonText: 'Aceptar',
    onConfirm: () => {
      this.modalOptionsService.cancel();
    },
    onCancel: () => {
      this.modalService.closeModal();
    }
  });
  }
 
}